/**
 * Created by Victor Rojo on 4/9/17.
 */
import { Injectable, Optional }                 from '@angular/core';
import { HttpClient, HttpResponse, HttpParams } from '@angular/common/http';
import { Router, NavigationExtras }             from '@angular/router';

import {interval as observableInterval, Unsubscribable as AnonymousSubscription } from 'rxjs';

import { Credential }                           from "./credential";

export class AuthServiceConfig{
  loginPath = "api/login";
  loginDemoPath = "api/login/demo";
  signUpPath = "api/signup";
  activatePath = "api/activate";
  passwordRecoverPath = "api/password/recovery";
  passwordResetPath = "api/password/reset";
  defaultRoute = ["main"];
}

@Injectable()
export class AuthService{
  private loginPath: string;
  private loginDemoPath: string;
  private signUpPath: string;
  private activatePath: string;
  private passwordRecoverPath: string;
  private passwordResetPath: string;
  private defaultRoute: any[];
  private toRouteAfterLogin: any[] = null;
  private _isLogged = false;
  private interval: AnonymousSubscription;
  private token: string;


  constructor(
    private http: HttpClient,
    private router: Router,
    @Optional() config: AuthServiceConfig) {
    if (config) {
      this.loginPath = config.loginPath;
      this.loginDemoPath = config.loginDemoPath;
      this.signUpPath = config.signUpPath;
      this.activatePath = config.activatePath;
      this.passwordRecoverPath = config.passwordRecoverPath;
      this.passwordResetPath = config.passwordResetPath;
      this.defaultRoute = config.defaultRoute;
      if ( localStorage.getItem("auth_token") ) {
        this._isLogged = true;
        this.token = localStorage.getItem("auth_token");
        this.setTokenCheckInterval();
      }
    }
  }

  private setTokenCheckInterval():void {
    this.interval = observableInterval(10000)
      .subscribe(() => {
        let token = localStorage.getItem("auth_token");
        if ( !token ) {
          this.logout(false);
        } else if ( this.token != token ) {
          location.reload();
        }
      });
  }

  private clearTokenCheckInterval():void {
    if ( this.interval ) {
      this.interval.unsubscribe()
    }
  }

  private errorHandler(error: any): Promise<any> {
    console.log('An error occurred', error);
    return Promise.reject(error.message || error);
  }

  private successHandler(response: LoginResponse){
    this._isLogged = true;
    let data = response;
    let token = this.parseJwt(data.token);
    this.token = data.token;
    localStorage.setItem("auth_token", data.token);
    localStorage.setItem("user_id", token.user_id);
    if ( data.vertical ) {
      localStorage.setItem("vertical", data.vertical);
    }
    this.setTokenCheckInterval();
    if ( this.toRouteAfterLogin ) {
      this.router.navigate(this.toRouteAfterLogin);
    } else {
      this.router.navigate(this.defaultRoute);
    }
    return response;
  }

  parseJwt (token:string) {
    let base64Url = token.split('.')[1];
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    let jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  };

  login(credential: Credential, rememberMe: boolean):Promise<any> {
    let params = new HttpParams();
    params = params.append('rememberme', rememberMe.toString());
    return this.http
      .post(this.loginPath, credential, { params: params })
      .toPromise()
      .then( (response:any) => this.successHandler(response) )
      .catch(error => this.errorHandler(error));
  }

  loginDemo(data:any):Promise<any> {
    return this.http
      .post(this.loginDemoPath, data)
      .toPromise()
      .then( (response:any) => this.successHandler(response) )
      .catch(error => this.errorHandler(error));
  }

  logout(expiredSession: boolean): void {
    this.clearTokenCheckInterval();
    if ( this._isLogged ) {
      this._isLogged = false;
      this.token = null;
      this.toRouteAfterLogin = null;
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user_id");
      localStorage.removeItem("vertical");
    }
    if ( expiredSession ) {
      this.toRouteAfterLogin = [this.router.url];
    }
    this.router.navigate(["login"]);
  }

  signup(data:any):Promise<any> {
  return this.http
      .post(this.signUpPath, data, { responseType: 'text' })
      .toPromise()
      .catch(error => this.errorHandler(error));
  }

  activate(data:any):Promise<any> {
    return this.http
      .post(this.activatePath, data, { responseType: 'text' })
      .toPromise()
      .catch(error => this.errorHandler(error));
  }

  passwordRecovery(data:any):Promise<any> {
    return this.http
      .post(this.passwordRecoverPath, data, { responseType: 'text' })
      .toPromise()
      .catch(error => this.errorHandler(error));
  }

  passwordReset(data:any):Promise<any> {
    return this.http
      .post(this.passwordResetPath, data, { responseType: 'text' })
      .toPromise()
      .catch(error => this.errorHandler(error));
  }

  isLogged(): boolean {
    return this._isLogged;
  }

  showLogin(commands: any[]): void {
    this.toRouteAfterLogin  = commands;
    this.router.navigate(["login"]);
  }

  getUserID():string{
    return localStorage.getItem("user_id");
  }

  getUserRoles():string[] {
    return this.getTokenField("scopes") || [];
  }

  getUserName():string {
    return this.getTokenField("sub");
  }

  getCloudDahsboard():boolean {
    return (this.getTokenField("flags") & 1) != 0;
  }

  getAccountID():string{
    return this.getTokenField("account_id");
  }

  getVertical():string{
    return localStorage.getItem("vertical");
  }

  private getTokenField(fieldName:string):any{
    let raw = localStorage.getItem("auth_token");
    if ( raw ) {
      let token = this.parseJwt(raw);
      return token[fieldName];
    }
    return "";
  }
}

interface LoginResponse{
  token: string;
  vertical: string;
}
